/**
 * Layout physique des plaques pour assembler un grand patron.
 * Cf. §4.4 du cahier des charges : grille logique unique, plaques calculées à la volée.
 */
export type PlateLayout = {
  /** Largeur d'une plaque physique en perles (ex: 29 pour la plaque standard Hama Midi). */
  plateWidth: number;
  /** Hauteur d'une plaque physique en perles. */
  plateHeight: number;
  /** Nombre de plaques en largeur (= ceil(grille.width / plateWidth)). */
  cols: number;
  /** Nombre de plaques en hauteur. */
  rows: number;
};

/**
 * Pas physique d'une perle Hama Midi : 5 mm = 0.5 cm.
 * Cf. §14.1 du cahier des charges.
 */
const BEAD_PITCH_CM = 0.5;

/**
 * Estimation empirique du temps de pose d'une perle, en secondes.
 *
 * Hypothèse : pose continue, perle par perle, débutant à l'aise.
 * Une œuvre de 20 000 perles (le cas réel cité au §1.2) donnerait
 * ~11 heures pures, ce qui colle à "plusieurs semaines" de loisir avec
 * sessions de 30-60 min.
 */
const SECONDS_PER_BEAD = 2;

export function computePlateLayout(
  gridWidth: number,
  gridHeight: number,
  plateWidth: number,
  plateHeight: number,
): PlateLayout {
  if (plateWidth <= 0 || plateHeight <= 0) {
    throw new Error(`Dimensions de plaque invalides : ${plateWidth}×${plateHeight}`);
  }
  return {
    plateWidth,
    plateHeight,
    cols: Math.max(1, Math.ceil(gridWidth / plateWidth)),
    rows: Math.max(1, Math.ceil(gridHeight / plateHeight)),
  };
}

export function physicalSize(gridWidth: number, gridHeight: number): { widthCm: number; heightCm: number } {
  return {
    widthCm: gridWidth * BEAD_PITCH_CM,
    heightCm: gridHeight * BEAD_PITCH_CM,
  };
}

export function estimatedTimeMinutes(totalBeads: number): number {
  if (totalBeads <= 0) return 0;
  return Math.ceil((totalBeads * SECONDS_PER_BEAD) / 60);
}

export type MissingColor = {
  colorId: string;
  needed: number;
  available: number;
  shortBy: number;
};

export type StockCoverage = {
  sufficient: boolean;
  missing: ReadonlyArray<MissingColor>;
};

/**
 * Compare ce que le patron requiert (`counts`) à l'inventaire déclaré par l'utilisateur.
 * `inventory` est typiquement le résultat de `combineInventories` du module catalog.
 *
 * Les couleurs avec count=0 dans le patron sont ignorées.
 */
export function computeStockCoverage(
  counts: ReadonlyMap<string, number>,
  inventory: ReadonlyMap<string, number>,
): StockCoverage {
  const missing: MissingColor[] = [];
  for (const [colorId, needed] of counts) {
    if (needed === 0) continue;
    const available = inventory.get(colorId) ?? 0;
    if (available < needed) {
      missing.push({ colorId, needed, available, shortBy: needed - available });
    }
  }
  return {
    sufficient: missing.length === 0,
    missing,
  };
}
